import { useState } from 'react';
import { Send } from 'lucide-react';
import styled from 'styled-components';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import SEO from '../components/seo/SEO';
import { useRegionalSeo } from '../hooks/useRegionalSeo';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${props => props.theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 2rem;
    
    @media (min-width: 768px) {
      padding-top: 3rem;
      padding-bottom: 3rem;
    }
    
    @media (min-width: 1024px) {
      padding-top: 4rem;
      padding-bottom: 4rem;
    }
  }
`;

const Content = styled.div`
  max-width: 48rem;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 2rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 0.75rem;
  
  @media (min-width: 768px) {
    font-size: 2.5rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 3rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 3.5rem;
  }
`;

const PageDescription = styled.p`
  font-size: 1rem;
  color: ${props => props.theme.colors.mutedForeground};
  margin-bottom: 2rem;
  line-height: 1.7;
  
  @media (min-width: 768px) {
    font-size: 1.125rem;
    margin-bottom: 3rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.25rem;
  }
`;

const ContactForm = styled.form`
  background-color: ${props => props.theme.colors.card};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: 2rem;
  
  @media (min-width: 768px) {
    padding: 2.5rem;
  }
  
  @media (min-width: 1024px) {
    padding: 3rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  @media (min-width: 1024px) {
    margin-bottom: 2rem;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.9375rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.colors.foreground};
  
  @media (min-width: 1024px) {
    font-size: 1rem;
    margin-bottom: 0.75rem;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 8rem;
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.input};
  background-color: ${props => props.theme.colors.background};
  padding: 0.75rem;
  font-size: 0.9375rem;
  font-family: ${props => props.theme.typography.fonts.sans};
  line-height: 1.5;
  color: ${props => props.theme.colors.foreground};
  resize: vertical;
  transition: all 0.2s;
  
  @media (min-width: 1024px) {
    font-size: 1rem;
    padding: 1rem;
    min-height: 10rem;
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.mutedForeground};
  }
  
  &:focus-visible {
    outline: 2px solid ${props => props.theme.colors.ring};
    outline-offset: 2px;
    border-color: ${props => props.theme.colors.ring};
  }
`;

const ErrorMessage = styled.p`
  color: ${props => props.theme.colors.error || '#ef4444'};
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SuccessMessage = styled.div`
  background-color: ${props => props.theme.colors.success || '#10b981'}22;
  border: 1px solid ${props => props.theme.colors.success || '#10b981'};
  color: ${props => props.theme.colors.success || '#10b981'};
  padding: 1rem;
  border-radius: ${props => props.theme.borderRadius.lg};
  margin-bottom: 1.5rem;
  font-size: 0.9375rem;
  
  @media (min-width: 1024px) {
    font-size: 1rem;
    padding: 1.25rem;
  }
`;

const ContactInfo = styled.div`
  margin-top: 3rem;
  padding-top: 3rem;
  border-top: 1px solid ${props => props.theme.colors.border};
  
  @media (min-width: 768px) {
    margin-top: 4rem;
    padding-top: 4rem;
  }
`;

const InfoTitle = styled.h3`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.25rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 1rem;
  
  @media (min-width: 1024px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const InfoItem = styled.div`
  margin-bottom: 0.75rem;
  font-size: 0.9375rem;
  color: ${props => props.theme.colors.mutedForeground};
  
  @media (min-width: 1024px) {
    font-size: 1rem;
    margin-bottom: 1rem;
  }
  
  strong {
    color: ${props => props.theme.colors.foreground};
    font-weight: 500;
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSuccess(false);
    
    // Validation
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.message.trim()) newErrors.message = 'Message is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setErrors({});
    }, 1000);
  };

  const regionalSeo = useRegionalSeo('/contact');

  return (
    <PageWrapper>
      <SEO
        title="Contact Us"
        description="Get in touch with the Bloomwik team — questions, suggestions, and partnership inquiries."
        url="/contact"
        language={regionalSeo.language}
        availableLanguages={regionalSeo.availableLanguages}
        alternateUrls={regionalSeo.alternateUrls}
      />
      <Header />
      <Main>
        <Container>
          <Content>
            <PageTitle>Contact Us</PageTitle>
            <PageDescription>
              Have a question, suggestion, or just want to say hello? We'd love to hear from you. 
              Fill out the form below and we'll get back to you as soon as possible.
            </PageDescription>

            {isSuccess && (
              <SuccessMessage>
                Thank you for your message! We'll get back to you soon.
              </SuccessMessage>
            )}

            <ContactForm onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your name"
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                />
                {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="subject">Subject *</Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What's this about?"
                />
                {errors.subject && <ErrorMessage>{errors.subject}</ErrorMessage>}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="message">Message *</Label>
                <TextArea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us what's on your mind..."
                />
                {errors.message && <ErrorMessage>{errors.message}</ErrorMessage>}
              </FormGroup>

              <Button type="submit" disabled={isSubmitting} size="lg">
                {isSubmitting ? 'Sending...' : (
                  <>
                    Send Message
                    <Send style={{ marginLeft: '0.5rem', width: '1rem', height: '1rem' }} />
                  </>
                )}
              </Button>
            </ContactForm>

            <ContactInfo>
              <InfoTitle>Get in Touch</InfoTitle>
              <InfoItem>
                <strong>Email:</strong> hello@bloomwik.com
              </InfoItem>
              <InfoItem>
                <strong>Response Time:</strong> We typically respond within 24-48 hours
              </InfoItem>
            </ContactInfo>
          </Content>
        </Container>
      </Main>
      <Footer />
    </PageWrapper>
  );
};

export default Contact;

